import React, { useEffect, useState, useRef, useCallback } from 'react';
import Box from '@mui/material/Box';
import Sidebar from './Sidebar';
import { useSelector, useDispatch } from 'react-redux';
import { selectAuthUser } from '../auth/store/auth.selectors';
import { getCompanyDetails } from '../company/store/company.actions';
import { selectedCompanyDetails } from '../company/store/company.selectors';
import { fetchDrivesState } from '../drive/store/drive.actions';
import {
  statedrivesJson,
  statedrivesLoading,
  statedrivesError,
} from '../drive/store/drive.selectors';

const US_GEOJSON_URL =
  'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';
const CA_GEOJSON_URL =
  'https://raw.githubusercontent.com/codeforamerica/click_that_hood/master/public/data/canada.geojson';

const INITIAL_CENTER = { lat: 39.8283, lng: -98.5795 };
const INITIAL_ZOOM = 4;

const GodViewMap = () => {
  const [mapLoaded, setMapLoaded] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [isLocked, setIsLocked] = useState(false);

  // Track which drive polyline is currently selected
  const [selectedDriveKey, setSelectedDriveKey] = useState(null);

  // Per-drive switch state: { [driveKey]: { Signs: bool, Lanes: bool, Rails: bool } }
  const [driveStates, setDriveStates] = useState({});

  const mapRef = useRef(null);
  const geocoderRef = useRef(null);
  const stateGeoJsonRef = useRef(null);
  const provinceGeoJsonRef = useRef(null);
  const isLockedRef = useRef(false);

  // Store polyline & marker objects keyed by driveKey for imperative show/hide
  // driveObjectsRef.current = { [driveKey]: { polyline, markers: [] } }
  const driveObjectsRef = useRef({});

  const dispatch = useDispatch();
  const user = useSelector(selectAuthUser);
  const companyName = useSelector(selectedCompanyDetails)?.companyName || 'Company';
  const drivesStateData = useSelector(statedrivesJson);
  const drivesLoading = useSelector(statedrivesLoading);
  const drivesError = useSelector(statedrivesError);

  // Keep ref in sync with state
  useEffect(() => {
    isLockedRef.current = isLocked;
  }, [isLocked]);

  useEffect(() => {
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) event.stopImmediatePropagation();
    };
    // eslint-disable-next-line no-console
    const originalError = console.error;
    // eslint-disable-next-line no-console
    console.error = (...args) => {
      if ((args?.[0]?.toString?.() || '').includes('ResizeObserver loop')) return;
      originalError.apply(console, args);
    };
    window.addEventListener('error', handleError);
    return () => {
      window.removeEventListener('error', handleError);
      // eslint-disable-next-line no-console
      console.error = originalError;
    };
  }, []);

  useEffect(() => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyBrYTn6FUEGr_84F1UPtsYa7l_sp4bWWMo&v=weekly&libraries=marker`;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);
    return () => {
      if (document.head.contains(script)) document.head.removeChild(script);
    };
  }, []);

  useEffect(() => {
    fetch(US_GEOJSON_URL)
      .then((r) => r.json())
      .then((d) => {
        stateGeoJsonRef.current = d;
      });
    fetch(CA_GEOJSON_URL)
      .then((r) => r.json())
      .then((d) => {
        provinceGeoJsonRef.current = d;
      });

    dispatch(getCompanyDetails(user.companyid));
  }, []);

  useEffect(() => {
    if (!mapLoaded) return;
    const mapDiv = document.getElementById('god-map');
    if (!mapDiv) return;

    mapRef.current = new window.google.maps.Map(mapDiv, {
      center: INITIAL_CENTER,
      zoom: INITIAL_ZOOM,
      mapId: '99b311845891871b',
      mapTypeControl: false,
      fullscreenControl: false,
      streetViewControl: false,
      zoomControl: false,
      panControl: false,
    });

    mapRef.current.data.setStyle({
      strokeColor: '#667eea',
      strokeOpacity: 1,
      strokeWeight: 2.5,
      fillColor: '#667eea',
      fillOpacity: 0.08,
      clickable: true, // will be set to false once locked
    });

    geocoderRef.current = new window.google.maps.Geocoder();

    let hoverTimer = null;
    let lastStateName = null;
    let isGeocoding = false;

    const mousemoveListener = mapRef.current.addListener('mousemove', (event) => {
      if (isLockedRef.current || isGeocoding) return;

      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(() => {
        const latLng = event.latLng;

        isGeocoding = true;
        geocoderRef.current.geocode({ location: latLng }, (results, status) => {
          isGeocoding = false;
          if (status !== 'OK' || !results?.length) return;

          const stateResult = results.find((r) => r.types.includes('administrative_area_level_1'));
          if (!stateResult) return;

          const stateName = stateResult.address_components.find((c) =>
            c.types.includes('administrative_area_level_1')
          )?.long_name;

          if (stateName && stateName !== lastStateName) {
            lastStateName = stateName;
            const countryComp = stateResult.address_components.find((c) =>
              c.types.includes('country')
            );
            highlightState(stateName, countryComp?.short_name, stateResult, false, false);
          }
        });
      }, 0);
    });

    const clickListener = mapRef.current.addListener('click', (event) => {
      if (isLockedRef.current) return;
      clearTimeout(hoverTimer);
      isGeocoding = true;

      const latLng = event.latLng;
      geocoderRef.current.geocode({ location: latLng }, (results, status) => {
        isGeocoding = false;

        if (status !== 'OK' || !results?.length) return;

        const stateResult = results.find((r) => r.types.includes('administrative_area_level_1'));
        if (!stateResult) return;

        const stateName = stateResult.address_components.find((c) =>
          c.types.includes('administrative_area_level_1')
        )?.long_name;
        const countryComp = stateResult.address_components.find((c) => c.types.includes('country'));

        lastStateName = stateName;
        highlightState(stateName, countryComp?.short_name, stateResult, true, true);
      });
    });

    const dataClickListener = mapRef.current.data.addListener('click', (event) => {
      window.google.maps.event.trigger(mapRef.current, 'click', event);
    });

    return () => {
      clearTimeout(hoverTimer);
      dataClickListener.remove();
      mousemoveListener.remove();
      clickListener.remove();
    };
  }, [mapLoaded]);

  // Build polylines + markers when drive data arrives.
  // Markers start hidden; they are shown/hidden via driveStates.
  useEffect(() => {
    if (!mapRef.current || !drivesStateData?.features?.length) return;

    // Tear down previous drive objects
    Object.values(driveObjectsRef.current).forEach(({ polyline, markers }) => {
      polyline.setMap(null);
      markers.forEach((m) => (m.map = null));
    });
    driveObjectsRef.current = {};

    // Reset per-drive state and selection
    setDriveStates({});
    setSelectedDriveKey(null);

    const sharedInfoWindow = new window.google.maps.InfoWindow();

    drivesStateData.features.forEach((feature, idx) => {
      if (feature.geometry?.type !== 'LineString') return;

      const driveKey = feature.properties?.driveName || `Drive ${idx + 1}`;
      const driveName = driveKey;

      const path = feature.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }));

      // ── Polyline ──────────────────────────────────────────────────────────────
      const polyline = new window.google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: '#004DA8',
        strokeOpacity: 0.85,
        strokeWeight: 3,
        map: mapRef.current,
      });

      const polylineInfoWindow = new window.google.maps.InfoWindow();

      polyline.addListener('mouseover', (e) => {
        polylineInfoWindow.setContent(`
          <div style="font-family: Inter, Roboto, Arial, sans-serif; padding-top: 10px; z-index: 1000;">
            <style>
              .gm-style-iw-chr { display: none !important; }
              .gm-style-iw button[aria-label="Close"] { display: none !important; }
              .gm-ui-hover-effect { display: none !important; }
            </style>
            <div style="font-weight:600; font-size:12px; color:#0f172a;">Drive Name</div>
            <div style="font-size:14px; font-weight:700; margin-bottom:8px; color:#0b61d6;">
              ${driveName}
            </div>
          </div>
        `);
        polylineInfoWindow.setPosition(e.latLng);
        polylineInfoWindow.open(mapRef.current);
      });

      polyline.addListener('mouseout', () => {
        polylineInfoWindow.close();
      });

      // Clicking a polyline selects that drive and defaults Signs ON
      polyline.addListener('click', () => {
        setSelectedDriveKey(driveKey);
        setDriveStates((prev) => {
          if (prev[driveKey]) return prev; // already has state, preserve it
          return {
            ...prev,
            [driveKey]: { Signs: true, Lanes: false, Rails: false },
          };
        });
      });

      // ── Event markers (hidden by default) ────────────────────────────────────
      const events = feature.properties?.events || [];
      const markers = [];

      events.forEach((event) => {
        if (!event.latitude || !event.longitude) return;

        const pinEl = document.createElement('div');
        pinEl.style.cssText = `
          width: 32px;
          height: 32px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          transition: transform 0.15s, filter 0.15s;
        `;

        const iconEl = document.createElement('div');
        iconEl.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <rect x="4" y="4" width="16" height="16" rx="2" fill="#facc15" stroke="white"
              stroke-width="1.5" transform="rotate(45 12 12)" />
            <text x="12" y="16" text-anchor="middle" font-size="10" font-weight="800"
              fill="#1e40af" font-family="Arial, sans-serif">!</text>
          </svg>
        `;
        pinEl.appendChild(iconEl);

        pinEl.addEventListener('mouseenter', () => {
          pinEl.style.transform = 'scale(1.25)';
          pinEl.style.filter = 'drop-shadow(0 4px 8px rgba(0,0,0,0.4))';
        });
        pinEl.addEventListener('mouseleave', () => {
          pinEl.style.transform = 'scale(1)';
          pinEl.style.filter = 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))';
        });

        const formattedTime = event.timestamp
          ? new Date(event.timestamp).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })
          : '—';

        const marker = new window.google.maps.marker.AdvancedMarkerElement({
          position: { lat: event.latitude, lng: event.longitude },
          map: null, // hidden by default, shown when drive is selected and Signs switch is ON
          content: pinEl,
          title: event.sign_name || 'Road Sign',
        });

        marker.element?.addEventListener('mouseenter', () => {
          sharedInfoWindow.setContent(`
            <div style="font-family: Inter, Roboto, Arial, sans-serif; padding: 10px 4px 6px; min-width: 180px;">
              <style>
                .gm-style-iw-chr { display: none !important; }
                .gm-style-iw button[aria-label="Close"] { display: none !important; }
                .gm-ui-hover-effect { display: none !important; }
              </style>
              <div style="display:flex; align-items:center; gap:6px; margin-bottom:8px;">
                <div style="width:8px; height:8px; border-radius:50%; background:#1e40af; flex-shrink:0;"></div>
                <span style="font-weight:700; font-size:13px; color:#0f172a;">Road Sign Event</span>
              </div>
              <div style="display:grid; grid-template-columns: auto 1fr; gap: 3px 10px; font-size:12px;">
                <span style="color:#64748b; font-weight:600;">Drive</span>
                <span style="color:#0f172a;">${driveName}</span>
                ${event.sign_name ? `<span style="color:#64748b; font-weight:600;">Sign</span><span style="color:#0f172a;">${event.sign_name}</span>` : ''}
                ${event.sign_type ? `<span style="color:#64748b; font-weight:600;">Type</span><span style="color:#0f172a;">${event.sign_type}</span>` : ''}
                ${event.sign_code ? `<span style="color:#64748b; font-weight:600;">Code</span><span style="color:#0f172a;">${event.sign_code}</span>` : ''}
                <span style="color:#64748b; font-weight:600;">Time</span>
                <span style="color:#0f172a;">${formattedTime}</span>
              </div>
            </div>
          `);
          sharedInfoWindow.open({ map: mapRef.current, anchor: marker });
        });

        marker.element?.addEventListener('mouseleave', () => {
          sharedInfoWindow.close();
        });

        markers.push(marker);
      });

      driveObjectsRef.current[driveKey] = { polyline, markers };
    });

    // Fit bounds to all drives
    const allCoords = drivesStateData.features.flatMap((f) => f.geometry?.coordinates || []);
    if (allCoords.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      allCoords.forEach(([lng, lat]) => bounds.extend({ lat, lng }));
      mapRef.current.fitBounds(bounds, 80);
    }
  }, [drivesStateData]);

  // Reactively show/hide markers based on driveStates.Signs
  useEffect(() => {
    Object.entries(driveObjectsRef.current).forEach(([driveKey, { markers }]) => {
      const showSigns = driveStates[driveKey]?.Signs === true;
      markers.forEach((m) => {
        m.map = showSigns ? mapRef.current : null;
      });
    });
  }, [driveStates]);

  // Highlight selected polyline visually
  useEffect(() => {
    Object.entries(driveObjectsRef.current).forEach(([driveKey, { polyline }]) => {
      const isSelected = driveKey === selectedDriveKey;
      polyline.setOptions({
        strokeColor: isSelected ? '#0038a8' : '#004DA8',
        strokeOpacity: isSelected ? 1 : 0.85,
        strokeWeight: isSelected ? 5 : 3,
        zIndex: isSelected ? 10 : 1,
      });
    });
  }, [selectedDriveKey]);

  const handleDriveSwitchToggle = useCallback((driveKey, switchName) => {
    setDriveStates((prev) => ({
      ...prev,
      [driveKey]: {
        ...prev[driveKey],
        [switchName]: !prev[driveKey]?.[switchName],
      },
    }));
  }, []);

  const handleSearch = (query) => {
    if (!geocoderRef.current) return;
    geocoderRef.current.geocode({ address: query }, (results, status) => {
      if (status !== 'OK' || !results?.length) return;
      const stateResult =
        results.find((r) => r.types.includes('administrative_area_level_1')) || results[0];
      const stateComponent = stateResult.address_components.find((c) =>
        c.types.includes('administrative_area_level_1')
      );
      const countryComponent = stateResult.address_components.find((c) =>
        c.types.includes('country')
      );
      highlightState(
        stateComponent?.long_name,
        countryComponent?.short_name,
        stateResult,
        true,
        true
      );
    });
  };

  const handleDriveRemove = useCallback((driveKey) => {
    // Hide markers for this drive (Signs off)
    const obj = driveObjectsRef.current[driveKey];
    if (obj) {
      obj.markers.forEach((m) => (m.map = null));
      // Reset polyline to default style
      obj.polyline.setOptions({
        strokeColor: '#004DA8',
        strokeOpacity: 0.85,
        strokeWeight: 3,
        zIndex: 1,
      });
    }
    // Remove only from the sidebar list; polyline stays on the map
    setDriveStates((prev) => {
      const next = { ...prev };
      delete next[driveKey];
      return next;
    });
    setSelectedDriveKey((prev) => (prev === driveKey ? null : prev));
  }, []);

  const handleClearState = () => {
    if (!mapRef.current) return;
    mapRef.current.data.forEach((f) => mapRef.current.data.remove(f));
    mapRef.current.data.setStyle({
      strokeColor: '#667eea',
      strokeOpacity: 1,
      strokeWeight: 2.5,
      fillColor: '#667eea',
      fillOpacity: 0.08,
      clickable: true, // re-enable for next hover/click selection
    });
    mapRef.current.setCenter(INITIAL_CENTER);
    mapRef.current.setZoom(INITIAL_ZOOM);
    setSelectedState(null);
    setIsLocked(false);
    isLockedRef.current = false;
    setSelectedDriveKey(null);
    setDriveStates({});

    Object.values(driveObjectsRef.current).forEach(({ polyline, markers }) => {
      polyline.setMap(null);
      markers.forEach((m) => (m.map = null));
    });
    driveObjectsRef.current = {};
  };

  const highlightState = (
    stateName,
    countryCode,
    stateResult,
    shouldFitBounds = false,
    lock = false
  ) => {
    mapRef.current.data.forEach((f) => mapRef.current.data.remove(f));

    const geoJson = countryCode === 'CA' ? provinceGeoJsonRef.current : stateGeoJsonRef.current;
    const feature = geoJson?.features.find(
      (f) => f.properties.name?.toLowerCase() === stateName?.toLowerCase()
    );

    if (feature) {
      mapRef.current.data.addGeoJson({ type: 'FeatureCollection', features: [feature] });
    }

    const countryNames = { US: 'United States', CA: 'Canada' };
    setSelectedState({ name: stateName, country: countryNames[countryCode] || countryCode });

    if (lock) {
      setIsLocked(true);
      isLockedRef.current = true;
      mapRef.current.data.setStyle({
        strokeColor: '#667eea',
        strokeOpacity: 1,
        strokeWeight: 2.5,
        fillColor: '#667eea',
        fillOpacity: 0.08,
        clickable: false, // makes the data layer transparent to mouse events, allowing polyline hover to work
      });
      dispatch(fetchDrivesState(stateName));
    }

    if (shouldFitBounds) {
      const bounds = stateResult.geometry.bounds || stateResult.geometry.viewport;
      if (bounds) mapRef.current.fitBounds(bounds, 100);
    }
  };

  return (
    <Box sx={{ width: '100%', height: '100%' }}>
      <Sidebar
        selectedState={selectedState}
        onSearch={handleSearch}
        companyName={companyName}
        onClearState={handleClearState}
        selectedDriveKey={selectedDriveKey}
        driveStates={driveStates}
        onDriveSwitchToggle={handleDriveSwitchToggle}
        onDriveRemove={handleDriveRemove}
      />

      {/* Loading overlay */}
      {drivesLoading && (
        <div
          style={{
            position: 'fixed',
            bottom: '2%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 18px',
            borderRadius: 999,
            background: 'rgba(15, 23, 42, 0.82)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
            color: '#fff',
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '0.01em',
            userSelect: 'none',
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            style={{ animation: 'spin 0.9s linear infinite', flexShrink: 0 }}
          >
            <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.25)" strokeWidth="2" />
            <path d="M8 2a6 6 0 0 1 6 6" stroke="#667eea" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Loading drives for {selectedState?.name}…
        </div>
      )}

      {/* Error toast */}
      {drivesError && !drivesLoading && (
        <div
          style={{
            position: 'fixed',
            bottom: '2%',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1100,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 18px',
            borderRadius: 999,
            background: 'rgba(185, 28, 28, 0.88)',
            backdropFilter: 'blur(8px)',
            boxShadow: '0 4px 24px rgba(185,28,28,0.25)',
            color: '#fff',
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontSize: 13,
            fontWeight: 500,
            letterSpacing: '0.01em',
            userSelect: 'none',
            maxWidth: 360,
          }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="8" cy="8" r="6" stroke="rgba(255,255,255,0.6)" strokeWidth="1.5" />
            <line
              x1="8"
              y1="5"
              x2="8"
              y2="8.5"
              stroke="white"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
            <circle cx="8" cy="11" r="0.9" fill="white" />
          </svg>
          Failed to load drives
          {drivesError?.message ? `: ${drivesError.message}` : ''}
        </div>
      )}

      {/* Top-right chip */}
      {selectedState && (
        <div
          style={{
            position: 'fixed',
            top: '10%',
            right: '1%',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 12px 8px 14px',
            borderRadius: 999,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            boxShadow: '0 4px 20px rgba(102,126,234,0.45)',
            color: '#fff',
            fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
            fontSize: 13,
            fontWeight: 600,
            letterSpacing: '0.01em',
            userSelect: 'none',
            animation: 'chipFadeIn 0.25s ease',
          }}
        >
          <span
            style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              background: '#fff',
              boxShadow: '0 0 0 0 rgba(255,255,255,0.6)',
              animation: 'pulse 1.8s infinite',
              flexShrink: 0,
            }}
          />
          <span
            style={{
              maxWidth: 180,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {selectedState.name}
          </span>
          {selectedState.country && (
            <span style={{ opacity: 0.7, fontSize: 11, fontWeight: 400 }}>
              - {selectedState.country}
            </span>
          )}
          <button
            onClick={handleClearState}
            aria-label="Clear selected state"
            style={{
              marginLeft: 4,
              width: 20,
              height: 20,
              borderRadius: '50%',
              border: 'none',
              background: 'rgba(255,255,255,0.2)',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
              flexShrink: 0,
              transition: 'background 0.18s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.38)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.2)')}
          >
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <line
                x1="1.5"
                y1="1.5"
                x2="8.5"
                y2="8.5"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
              <line
                x1="8.5"
                y1="1.5"
                x2="1.5"
                y2="8.5"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
      )}

      <style>{`
        @keyframes chipFadeIn {
          from { opacity: 0; transform: translateY(-6px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes pulse {
          0%   { box-shadow: 0 0 0 0 rgba(255,255,255,0.6); }
          70%  { box-shadow: 0 0 0 5px rgba(255,255,255,0); }
          100% { box-shadow: 0 0 0 0 rgba(255,255,255,0); }
        }
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <Box id="god-map" sx={{ position: 'absolute', width: '100%', height: '100%' }} />
    </Box>
  );
};

export default GodViewMap;
